/**
 * Fygg · Simple Bundles option picker
 *
 * Drives snippets/fygg-bundle-options.liquid and snippets/fygg-bundle-stage.liquid.
 *
 * Three jobs:
 *
 *  1. Keep the cart payload correct. The Simple Bundles contract is three inputs
 *     inside #AddToCartForm — properties[<Option name>] per group, plus
 *     properties[_bundle_selection] with values joined by " ++ " within a group and
 *     groups joined by " <> ". Liquid renders them with each group's default; this
 *     rewrites them on every pick, so the payload is right with or without the app.
 *
 *  2. Survive two things that rewrite the DOM underneath us. Vue re-renders the
 *     whole product-form subtree (so state is held here, keyed by variant id, and
 *     listeners are delegated on document), and the Simple Bundles embed re-injects
 *     its widget on DOMContentLoaded, again at +500ms, on pageshow, and on every
 *     input[name="id"] mutation — each time resetting its <select>s to their default
 *     and overwriting the hidden inputs it adopted from us.
 *
 *  3. Keep the app itself in agreement. The shopper's choice is pushed into the
 *     app's own <select> and a change event dispatched, so when the app is present
 *     it recomputes _bundle_selection to the same string we did. If its CDN script
 *     never loads, our server-rendered inputs already carry the answer.
 *
 * The app's widget is parked in a hidden [data-simple-bundles-options] sink that
 * template--product.liquid renders outside #AddToCartForm, so its <select>s are
 * never serialized into the cart POST.
 */
(function () {
  'use strict';

  /* Loaded globally from layout/theme.liquid (the quick-view drawer injects bundle
     forms into pages that never loaded it); never register the listeners twice. */
  if (window.__fyggBundleOptionsLoaded) return;
  window.__fyggBundleOptionsLoaded = true;

  var SELECTED = {}; // variantId -> { optionName: value }

  function parseGroups(root) {
    try {
      return JSON.parse(root.getAttribute('data-groups') || '[]');
    } catch (e) {
      return [];
    }
  }

  function stateFor(root) {
    var id = root.getAttribute('data-variant-id') || 'default';
    if (!SELECTED[id]) SELECTED[id] = {};
    return SELECTED[id];
  }

  function radios(root) {
    return Array.prototype.slice.call(root.querySelectorAll('input[data-fygg-bundle-option]'));
  }

  /* The value a group falls back to when the shopper has not picked: whatever
     Liquid server-rendered as checked. */
  function defaultValue(root, optionName) {
    var first = root.querySelector('input[data-fygg-bundle-option][data-option-name="' + cssEscape(optionName) + '"]');
    if (first) return first.value;
    var prop = root.querySelector('[data-fygg-bundle-prop="' + cssEscape(optionName) + '"]');
    return prop ? prop.value : '';
  }

  function cssEscape(value) {
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function valueFor(root, state, optionName) {
    return state[optionName] || defaultValue(root, optionName);
  }

  function selectionString(root, state) {
    return parseGroups(root)
      .map(function (group) {
        return group
          .map(function (optionName) {
            return valueFor(root, state, optionName);
          })
          .join(' ++ ');
      })
      .join(' <> ');
  }

  function setText(el, text) {
    if (el && text != null && el.textContent !== text) el.textContent = text;
  }

  /**
   * Format cents with the shop's money_format, dropping ".00" the way Liquid's
   * money_without_trailing_zeros does — so a JS-updated price matches the one
   * Liquid rendered next to it rather than gaining decimals on first interaction.
   */
  function money(root, cents) {
    var format = root.getAttribute('data-money-format') || '${{amount}}';
    var amount = (cents / 100).toFixed(2);
    if (amount.slice(-3) === '.00') amount = amount.slice(0, -3);
    var withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format.replace(/\{\{\s*amount[^}]*\}\}/g, withCommas);
  }

  function toggle(el, on) {
    if (el) el.hidden = !on;
  }

  function setImage(img, src, alt) {
    if (!img || !src || img.getAttribute('src') === src) return;
    img.setAttribute('src', src);
    if (alt != null) img.setAttribute('alt', alt);
  }

  /* Mirror the choice into the app's own <select> so that, whenever the embed is
     loaded, it recomputes its inputs to the same values instead of fighting us. */
  function syncApp(optionName, value) {
    var sink = document.querySelector('[data-simple-bundles-options]');
    if (!sink) return;
    var select = sink.querySelector('select[name="properties[' + cssEscape(optionName) + ']"]');
    if (!select || select.value === value) return;
    var match = Array.prototype.slice.call(select.options).some(function (opt) {
      return opt.value === value;
    });
    if (!match) return;
    select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function apply(root) {
    var state = stateFor(root);

    radios(root).forEach(function (radio) {
      var optionName = radio.getAttribute('data-option-name');
      var isCurrent = radio.value === valueFor(root, state, optionName);
      if (radio.checked !== isCurrent) radio.checked = isCurrent;
      var pill = radio.closest('.fygg-bundle-pill');
      if (pill) pill.classList.toggle('is-active', isCurrent);
      if (!isCurrent) return;

      var group = radio.getAttribute('data-group');
      var data = radio.dataset;

      setText(root.querySelector('[data-fygg-bundle-group="' + cssEscape(group) + '"] [data-fygg-bundle-current]'), data.label);

      var item = root.querySelector('[data-fygg-bundle-item="' + cssEscape(group) + '"]');
      if (item) {
        setImage(item.querySelector('[data-fygg-bundle-item-thumb]'), data.thumb, '');
        setText(item.querySelector('[data-fygg-bundle-item-name]'), data.itemName);
        setText(item.querySelector('[data-fygg-bundle-item-meta]'), data.label);
        setText(item.querySelector('[data-fygg-bundle-item-price]'), data.price);
        /* A component with no reviews yet hides the star rather than showing a zero. */
        setText(item.querySelector('[data-fygg-bundle-item-rating-text]'), data.rating || '');
        toggle(item.querySelector('[data-fygg-bundle-item-rating]'), !!data.rating);
      }

      /* The stage lives in the other grid column, outside this root. */
      setImage(document.querySelector('[data-fygg-bundle-tile-image="' + cssEscape(group) + '"]'), data.image, data.imageAlt);
      setText(document.querySelector('[data-fygg-bundle-tile-name="' + cssEscape(group) + '"]'), data.itemName);
      setText(document.querySelector('[data-fygg-bundle-tile-label="' + cssEscape(group) + '"]'), data.label);
    });

    parseGroups(root).forEach(function (group) {
      group.forEach(function (optionName) {
        var value = valueFor(root, state, optionName);
        var input = root.querySelector('[data-fygg-bundle-prop="' + cssEscape(optionName) + '"]');
        if (input && input.value !== value) input.value = value;
        syncApp(optionName, value);
      });
    });

    var selection = root.querySelector('[data-fygg-bundle-selection]');
    if (selection) {
      var next = selectionString(root, state);
      if (selection.value !== next) selection.value = next;
    }

    applyPrice(root);
  }

  /**
   * "vs buying separately": the components' own prices summed for the current pick,
   * against the bundle's price. Recomputed here because a pricier option changes what
   * the separate total would have been.
   *
   * Skipped when the variant has a real compare_at_price — a merchant-set compare
   * wins over a derived one, and it does not move with the selection.
   */
  function applyPrice(root) {
    if (root.getAttribute('data-compare-fixed') === 'true') return;

    var compareEl = document.querySelector('[data-fygg-bundle-compare]');
    var savingsEl = document.querySelector('[data-fygg-bundle-savings]');
    if (!compareEl && !savingsEl) return;

    var price = parseInt(root.getAttribute('data-price-cents'), 10) || 0;
    var separate = parseInt(root.getAttribute('data-fixed-cents'), 10) || 0;
    radios(root).forEach(function (radio) {
      if (radio.checked) separate += parseInt(radio.dataset.itemCents, 10) || 0;
    });

    var saving = separate - price;
    if (compareEl) {
      setText(compareEl.querySelector('.money'), money(root, separate));
      toggle(compareEl, saving > 0);
    }
    if (savingsEl) {
      setText(savingsEl.querySelector('.bundle-savings-badge__amount'), money(root, saving));
      toggle(savingsEl, saving > 0);
    }
  }

  function applyAll() {
    document.querySelectorAll('[data-fygg-bundle]').forEach(apply);
  }

  document.addEventListener('change', function (event) {
    var radio = event.target;
    if (!radio || !radio.matches || !radio.matches('input[data-fygg-bundle-option]')) return;
    var root = radio.closest('[data-fygg-bundle]');
    if (!root) return;
    stateFor(root)[radio.getAttribute('data-option-name')] = radio.value;
    apply(root);
  });

  /* The app rewrites the sink's innerHTML on every one of its re-runs, which is the
     one signal that covers all of them. Re-assert after it settles. */
  function watchSink() {
    var sink = document.querySelector('[data-simple-bundles-options]');
    if (!sink || sink.dataset.fyggWatched) return;
    sink.dataset.fyggWatched = '1';
    new MutationObserver(function () {
      window.requestAnimationFrame(applyAll);
    }).observe(sink, { childList: true, subtree: true });
  }

  /* Vue rebuilds the product-form subtree, taking our markup with it. */
  function watchForm() {
    var scope = document.querySelector('.product-form-vue') || document.body;
    if (!scope || scope.dataset.fyggBundleWatched) return;
    scope.dataset.fyggBundleWatched = '1';
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () {
        pending = false;
        applyAll();
      });
    }).observe(scope, { childList: true, subtree: true });
  }

  function init() {
    /* Seed from whatever Liquid rendered checked, so a reload with no interaction
       still reports a complete selection. */
    document.querySelectorAll('[data-fygg-bundle]').forEach(function (root) {
      var state = stateFor(root);
      radios(root).forEach(function (radio) {
        if (radio.checked && !state[radio.getAttribute('data-option-name')]) {
          state[radio.getAttribute('data-option-name')] = radio.value;
        }
      });
    });
    watchSink();
    watchForm();
    applyAll();
  }

  /**
   * The line item properties for the current selection, for anything that builds its
   * own /cart/add.js payload instead of serializing the form — sections/fygg-sticky-atc.liquid
   * does exactly that. Returns null when this product is not a bundle.
   */
  window.fyggBundleSelection = function () {
    var root = document.querySelector('[data-fygg-bundle]');
    if (!root) return null;
    var state = stateFor(root);
    var properties = {};
    parseGroups(root).forEach(function (group) {
      group.forEach(function (optionName) {
        properties[optionName] = valueFor(root, state, optionName);
      });
    });
    properties._bundle_selection = selectionString(root, state);
    return properties;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  window.addEventListener('pageshow', applyAll);

  /* The theme editor replaces the whole section, taking the sink and the element the
     form observer was attached to with it, so re-init rather than just re-apply. */
  document.addEventListener('shopify:section:load', init);
})();
